import * as React from 'react';
import { AffectedCustomer, CustomerAgreementInput } from '../types';
import { useCheckDeactivationImpact } from '../queries/useCheckDeactivationImpact';

type SaveInput = CustomerAgreementInput & { isActive?: boolean };

type PendingDeactivation = {
    id: number;
    input: SaveInput;
    customers: AffectedCustomer[];
};

type UseConfirmedSaveArgs = {
    save: (id: number | null, input: SaveInput) => Promise<boolean>;
    closeEditor: () => void;
};

export function useConfirmedSave({ save, closeEditor }: UseConfirmedSaveArgs) {
    const checkDeactivationImpact = useCheckDeactivationImpact();
    const [pendingDeactivation, setPendingDeactivation] =
        React.useState<PendingDeactivation | null>(null);
    const [checkFailed, setCheckFailed] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    async function saveAndClose(id: number | null, input: SaveInput) {
        let saved = false;
        setSaving(true);
        try {
            saved = await save(id, input);
        } finally {
            setSaving(false);
        }
        if (saved) {
            closeEditor();
        }
    }

    async function requestSave(id: number | null, wasActive: boolean, input: SaveInput) {
        if (id === null || !wasActive || input.isActive !== false) {
            await saveAndClose(id, input);
            return;
        }

        let customers: AffectedCustomer[];
        setSaving(true);
        try {
            customers = (await checkDeactivationImpact(id)).customers;
        } catch (error) {
            setCheckFailed(true);
            return;
        } finally {
            setSaving(false);
        }

        if (customers.length === 0) {
            await saveAndClose(id, input);
            return;
        }

        setPendingDeactivation({ id, input, customers });
    }

    async function confirmDeactivation() {
        if (pendingDeactivation === null) {
            return;
        }

        setPendingDeactivation(null);
        await saveAndClose(pendingDeactivation.id, pendingDeactivation.input);
    }

    return {
        requestSave,
        pendingDeactivation,
        saving,
        confirmDeactivation,
        cancelDeactivation: () => setPendingDeactivation(null),
        checkFailed,
        dismissCheckFailed: () => setCheckFailed(false)
    };
}
